'use strict';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getFocusableElements = (element) => Array.from(element.querySelectorAll(FOCUSABLE_SELECTOR));
const getDialogContent = (dialog) => dialog.querySelector('[data-video-carousel-modal-content]');

const stopDialogMedia = (dialog) => {
    const content = getDialogContent(dialog);

    if (!content) {
        return;
    }

    content.querySelectorAll('iframe').forEach((iframe) => {
        const currentSrc = iframe.getAttribute('src');

        if (!currentSrc) {
            return;
        }

        iframe.setAttribute('data-video-carousel-src', currentSrc);
        iframe.setAttribute('src', '');
    });

    content.querySelectorAll('video').forEach((video) => {
        if (typeof video.pause === 'function') {
            video.pause();
        }

        try {
            video.currentTime = 0;
        } catch (error) {
            // Ignore currentTime reset failures for protected media.
        }
    });
};

const restoreDialogMedia = (dialog) => {
    const content = getDialogContent(dialog);

    if (!content) {
        return;
    }

    content.querySelectorAll('iframe').forEach((iframe) => {
        const storedSrc = iframe.getAttribute('data-video-carousel-src');

        if (!storedSrc || iframe.getAttribute('src') === storedSrc) {
            return;
        }

        iframe.setAttribute('src', storedSrc);
    });
};

const initVideoCarouselModal = () => {
    const carousels = document.querySelectorAll('.video-carousel, .saloon-doors');
    const root = document.documentElement;

    if (!carousels.length) {
        return;
    }

    carousels.forEach((carousel) => {
        const dialogs = Array.from(carousel.querySelectorAll('dialog[data-video-carousel-modal]'));

        if (!dialogs.length) {
            return;
        }

        const syncScrollLock = () => {
            const hasOpenDialog = dialogs.some((dialog) => dialog.open || dialog.hasAttribute('open'));
            root.classList.toggle('video-carousel-modal-open', hasOpenDialog);
        };

        const setClosedState = (dialog, restoreFocus = true) => {
            dialog.setAttribute('hidden', 'hidden');
            dialog.hidden = true;
            dialog.setAttribute('aria-hidden', 'true');
            dialog.removeAttribute('open');

            if (restoreFocus && dialog.__activeTrigger) {
                dialog.__activeTrigger.focus({ preventScroll: true });
            }

            dialog.__activeTrigger = null;
            syncScrollLock();
        };

        const openDialog = (dialog, trigger) => {
            const supportsNativeDialog = typeof dialog.showModal === 'function' && typeof dialog.close === 'function';
            const closeButton = dialog.querySelector('[data-video-carousel-close]');

            dialog.__activeTrigger = trigger;
            restoreDialogMedia(dialog);
            dialog.hidden = false;
            dialog.removeAttribute('hidden');
            dialog.setAttribute('aria-hidden', 'false');

            if (supportsNativeDialog) {
                if (!dialog.open) {
                    try {
                        dialog.showModal();
                    } catch (error) {
                        dialog.setAttribute('open', 'open');
                    }
                }
            } else {
                dialog.classList.add('video-carousel__modal--fallback');
                dialog.setAttribute('role', 'dialog');
                dialog.setAttribute('open', 'open');
            }

            syncScrollLock();

            if (closeButton) {
                closeButton.focus({ preventScroll: true });
                return;
            }

            const focusableElements = getFocusableElements(dialog);
            if (focusableElements.length) {
                focusableElements[0].focus({ preventScroll: true });
            }
        };

        const closeDialog = (dialog) => {
            const supportsNativeDialog = typeof dialog.showModal === 'function' && typeof dialog.close === 'function';

            stopDialogMedia(dialog);

            if (supportsNativeDialog && dialog.open) {
                dialog.close();
                return;
            }

            setClosedState(dialog);
        };

        dialogs.forEach((dialog) => {
            const supportsNativeDialog = typeof dialog.showModal === 'function' && typeof dialog.close === 'function';

            setClosedState(dialog, false);

            dialog.addEventListener('click', (event) => {
                const closeButton = event.target.closest('[data-video-carousel-close]');
                if (closeButton && dialog.contains(closeButton)) {
                    closeDialog(dialog);
                    return;
                }

                if (event.target === dialog) {
                    closeDialog(dialog);
                }
            });

            if (supportsNativeDialog) {
                dialog.addEventListener('close', () => setClosedState(dialog));
                return;
            }

            dialog.addEventListener('keydown', (event) => {
                if (event.key === 'Escape' && (dialog.open || dialog.hasAttribute('open'))) {
                    closeDialog(dialog);
                    return;
                }

                if (event.key !== 'Tab' || !(dialog.open || dialog.hasAttribute('open'))) {
                    return;
                }

                const focusableElements = getFocusableElements(dialog);
                if (!focusableElements.length) {
                    event.preventDefault();
                    return;
                }

                const firstFocusable = focusableElements[0];
                const lastFocusable = focusableElements[focusableElements.length - 1];

                if (event.shiftKey && document.activeElement === firstFocusable) {
                    event.preventDefault();
                    lastFocusable.focus();
                } else if (!event.shiftKey && document.activeElement === lastFocusable) {
                    event.preventDefault();
                    firstFocusable.focus();
                }
            });
        });

        carousel.querySelectorAll('[data-video-carousel-open]').forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault();

                const targetId = trigger.getAttribute('data-video-carousel-target') || trigger.getAttribute('aria-controls');
                if (!targetId) {
                    return;
                }

                const targetDialog = document.getElementById(targetId);
                if (!targetDialog || !carousel.contains(targetDialog) || targetDialog.tagName !== 'DIALOG') {
                    return;
                }

                openDialog(targetDialog, trigger);
            });
        });
    });
};

export default initVideoCarouselModal;
